import React, { useEffect, useState, useRef } from "react";
import { getAuth, updateProfile } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from "firebase/firestore"; 
import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db } from "../firebase";
import Spinner from "../components/Spinner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { 
  FaBuilding,
  FaCamera,
  FaClock, 
  FaCodeBranch, 
  FaComments,
  FaCommentAlt,
  FaEdit, 
  FaEnvelope, 
  FaFacebook, 
  FaGlobe, 
  FaGraduationCap,
  FaHandsHelping, 
  FaHome,
  FaInfoCircle, 
  FaInstagram, 
  FaLink, 
  FaLinkedin, 
  FaMapMarkerAlt, 
  FaPhone, 
  FaPlus, 
  FaSave, 
  FaTimes, 
  FaTools, 
  FaTrash, 
  FaTwitter, 
  FaUpload, 
  FaUserFriends,
  FaUsers, 
  FaYoutube,
  FaImage,
  FaClipboardCheck
} from 'react-icons/fa';
import { Switch } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';

// Custom components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 ${className}`}>
    {children}
  </div>
);

const Section = ({ title, children, className = '' }) => (
  <div className={`mb-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
      {title}
    </h3>
    <div className="bg-gray-50 p-4 rounded-lg">
      {children}
    </div>
  </div>
);

// Service categories for the services tab
const serviceCategories = [
  'Assessment',
  'Behavioral Therapy',
  'Speech Therapy',
  'Occupational Therapy',
  'Special Education',
  'Parent Training',
  'Social Skills',
  'Counseling',
  'Other'
];

const orgTypeOptions = [
  { value: "", label: "Select Organization Type..." },
  { value: "school", label: "School" },
  { value: "cdc", label: "CDC" },
  { value: "special_school", label: "Special School" },
  { value: "ngo", label: "NGO" },
  { value: "hospital", label: "Hospital" },
  { value: "other", label: "Other" },
];

const emptyOrgProfile = {
  // Basic Info
  orgName: "",
  email: "",
  mobile: "",
  city: "",
  localArea: "",
  orgType: "",
  description: "",
  
  // Media
  logoUrl: "",
  bannerUrl: "",
  
  // About
  aboutUs: "",
  impact: "",
  yearFounded: "",
  
  // Services
  services: [],
  
  // Contact
  website: "",
  contactEmail: "",
  contactPhone: "",
  workingHours: "9:00 AM - 5:00 PM",
  workingDays: "Monday - Friday",
  
  // Social Media
  socialMedia: {
    facebook: "",
    twitter: "",
    linkedin: "",
    instagram: "",
    youtube: ""
  },
  
  // Locations
  locations: [],
  
  // Settings
  isPublic: false,
  profileUpdatedAt: null,
  searchKeywords: []
};

export default function OrganizationProfile() {
  const [user, setUser] = useState(null);
  const [orgProfile, setOrgProfile] = useState(emptyOrgProfile);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState({ show: false, message: "", type: "success" });
  const [error, setError] = useState("");
  const [incomplete, setIncomplete] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  // Form state
  const [currentService, setCurrentService] = useState({
    name: '',
    description: '',
    category: '',
    isOfferedInPerson: false,
    isOfferedOnline: false
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentServiceInput, setCurrentServiceInput] = useState("");
  const [currentLocation, setCurrentLocation] = useState({ 
    name: '', 
    address: '', 
    city: '', 
    state: '', 
    country: 'India', 
    postalCode: '', 
    phone: '' 
  });
  
  // UI state
  const [activeTab, setActiveTab] = useState('overview');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadType, setUploadType] = useState(''); // 'logo' or 'banner'
  const [isPublicView, setIsPublicView] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const storage = getStorage();
  
  // Add state for subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && !initialFetchDone) {
      fetchProfile(user);
      setInitialFetchDone(true);
    }
  }, [user, initialFetchDone]); 

  const fetchProfile = async (currentUser) => {
    setLoading(true);
    setError("");
    try {
      const orgRef = doc(db, "organizations", currentUser.uid);
      const orgSnap = await getDoc(orgRef);
      let orgData = orgSnap.exists() ? orgSnap.data() : {};
      
      const isNewProfile = !orgSnap.exists();

      // Convert any string-based services to the new object format
      const services = Array.isArray(orgData.services) 
        ? orgData.services.map(service => 
            typeof service === 'string' 
              ? {
                  name: service,
                  description: '',
                  category: 'Other',
                  isOfferedInPerson: false,
                  isOfferedOnline: false
                }
              : service
          )
        : [];

      setOrgProfile({
        orgName: currentUser.displayName || orgData.orgName || "",
        email: currentUser.email || orgData.email || "",
        mobile: orgData.mobile || "",
        city: orgData.city || "",
        localArea: orgData.localArea || "",
        orgType: orgData.orgType || "",
        description: orgData.description || "",
        aboutUs: orgData.aboutUs || "",
        impact: orgData.impact || "",
        yearFounded: orgData.yearFounded || "",
        services: services,
        website: orgData.website || "",
        contactEmail: orgData.contactEmail || "",
        contactPhone: orgData.contactPhone || "",
        workingHours: orgData.workingHours || "9:00 AM - 5:00 PM",
        workingDays: orgData.workingDays || "Monday - Friday",
        socialMedia: orgData.socialMedia || {
          facebook: "",
          twitter: "",
          linkedin: "",
          instagram: "",
          youtube: ""
        },
        numBranches: orgData.numBranches || "",
        locations: Array.isArray(orgData.locations) ? orgData.locations : [],
        isPublic: orgData.isPublic || false,
        profileUpdatedAt: orgData.profileUpdatedAt || null,
        searchKeywords: orgData.searchKeywords || [],
        logoUrl: orgData.logoUrl || ""
      });

      const requiredFields = [orgProfile.orgName, orgProfile.mobile, orgProfile.city, orgProfile.orgType];
      const isProfileIncomplete = requiredFields.some(field => !field || field.trim() === "");
      setIncomplete(isProfileIncomplete);

      if (isNewProfile || isProfileIncomplete) {
        setEditMode(true);
      }

    } catch (e) {
      setError("Failed to fetch profile. " + e.message);
      setShowToast({ show: true, message: "Error fetching profile: " + e.message, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = (e) => {
    const { name, value } = e.target;
    setOrgProfile((p) => ({ ...p, [name]: value }));
  };

  // For the new service input field - keeping this for backward compatibility
  const handleCurrentServiceInputChange = (e) => {
    setCurrentServiceInput(e.target.value);
  };

  // This is the old string-based service handler - keeping for backward compatibility
  const handleAddSimpleService = () => {
    const newService = currentServiceInput.trim();
    if (newService) {
      // Convert simple string to service object
      const serviceObj = {
        name: newService,
        description: '',
        category: 'Other',
        isOfferedInPerson: false,
        isOfferedOnline: false
      };
      
      // Check if service with this name already exists
      const exists = orgProfile.services.some(s => 
        typeof s === 'string' ? s === newService : s.name === newService
      );
      
      if (!exists) {
        setOrgProfile(p => ({
          ...p,
          services: [...p.services, serviceObj]
        }));
      }
    }
    setCurrentServiceInput("");
  };

  // This is the old string-based remove handler - keeping for backward compatibility
  const handleRemoveService = (serviceToRemove) => {
    setOrgProfile(p => ({
      ...p,
      services: p.services.filter(service => 
        typeof service === 'string' 
          ? service !== serviceToRemove 
          : service.name !== serviceToRemove
      )
    }));
  };
  
  const handleOrgSave = async (e) => {
    e.preventDefault();
    if (!user) {
      const errorMsg = "No user logged in.";
      console.error(errorMsg);
      setError(errorMsg);
      setShowToast({ show: true, message: "Error: Not logged in.", type: "error" });
      return;
    }
    
    // Validate required fields
    if (!orgProfile.orgName || !orgProfile.orgType) {
      const errorMsg = "Organization name and type are required";
      console.error(errorMsg, { orgName: orgProfile.orgName, orgType: orgProfile.orgType });
      setError(errorMsg);
      setShowToast({ show: true, message: errorMsg, type: "error" });
      return;
    }
    
    setSaving(true);
    setError("");
    
    try {
      console.log("Starting save operation...", { userId: user.uid });
      
      // Prepare the data to be saved
      const profileDataToSave = {
        orgName: orgProfile.orgName.trim(),
        orgType: orgProfile.orgType,
        // Only include other fields if they exist to avoid overwriting with undefined
        ...(orgProfile.aboutUs && { aboutUs: orgProfile.aboutUs }),
        ...(orgProfile.yearFounded && { yearFounded: orgProfile.yearFounded }),
        ...(orgProfile.teamSize && { teamSize: orgProfile.teamSize }),
        ...(orgProfile.impact && { impact: orgProfile.impact }),
        profileUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        uid: user.uid,
      };
      
      console.log("Prepared profile data:", profileDataToSave);

      // Try to save to organizations collection first
      try {
        console.log("Attempting to save to organizations collection...");
        const orgRef = doc(db, "organizations", user.uid);
        await setDoc(orgRef, profileDataToSave, { merge: true });
        console.log("Successfully saved to organizations collection");
      } catch (orgError) {
        console.error("Error saving to organizations collection:", orgError);
        throw new Error(`Failed to save organization data: ${orgError.message}`);
      }

      // Update users collection
      try {
        console.log("Updating users collection...");
        const userRef = doc(db, "users", user.uid);
        const userUpdate = {
          displayName: profileDataToSave.orgName,
          orgType: profileDataToSave.orgType,
          updatedAt: serverTimestamp(),
        };
        console.log("User update data:", userUpdate);
        await setDoc(userRef, userUpdate, { merge: true });
        console.log("Successfully updated users collection");
      } catch (userError) {
        console.error("Error updating users collection:", userError);
        // Don't throw here, as the org data might have been saved successfully
      }

      // Update Firebase Auth user profile if name changed
      try {
        const auth = getAuth();
        if (user.displayName !== profileDataToSave.orgName) {
          console.log("Updating auth profile display name...");
          await updateProfile(auth.currentUser, {
            displayName: profileDataToSave.orgName
          });
          console.log("Successfully updated auth profile");
        }
      } catch (authError) {
        console.error("Error updating auth profile:", authError);
        // Don't throw here, as the data might have been saved successfully
      }

      // If we got here, everything was successful
      setEditMode(false);
      const successMsg = "Profile saved successfully!";
      console.log(successMsg);
      setShowToast({ show: true, message: successMsg, type: "success" });
      
      // Auto-dismiss the success message after 3 seconds
      const timer = setTimeout(() => {
        setShowToast(prev => ({ ...prev, show: false }));
      }, 3000);
      
      // Cleanup timer on component unmount
      return () => clearTimeout(timer);
      
    } catch (e) {
      const errorMsg = `Failed to save profile: ${e.message}`;
      console.error(errorMsg, e);
      setError(errorMsg);
      setShowToast({ 
        show: true, 
        message: errorMsg, 
        type: "error" 
      });
      
      // Auto-dismiss the error message after 5 seconds
      setTimeout(() => {
        setShowToast(prev => ({ ...prev, show: false }));
      }, 5000);
    } finally {
      setSaving(false);
    }
  };

  // Handle file upload (logo or banner)
  const handleFileUpload = async (file, type = 'logo') => {
    if (!file) return null;
    
    try {
      setUploadType(type);
      setIsUploading(true);
      setUploadProgress(0);
      
      // Set the correct path based on file type
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}-${Date.now()}.${fileExt}`;
      const storagePath = type === 'logo' 
        ? `org-logos/${user.uid}/${fileName}`
        : `org-banners/${user.uid}/${fileName}`;
      
      const storageRef = ref(storage, storagePath);
      
      // Set metadata for the upload
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedBy: user.uid,
          uploadedAt: new Date().toISOString()
        }
      };
      
      // Use uploadBytesResumable for progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error(`Upload failed (${type}):`, error);
            setError(`Failed to upload ${type}. Please try again.`);
            setIsUploading(false);
            setUploadProgress(0);
            reject(error);
          },
          async () => {
            try {
              // Get the download URL
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              
              // Update the appropriate URL in Firestore
              const updateData = {
                [`${type}Url`]: downloadURL,
                updatedAt: serverTimestamp()
              };
              
              await updateDoc(doc(db, "organizations", user.uid), updateData);
              
              // Update local state with the new URL
              setOrgProfile(prev => ({
                ...prev,
                [type + 'Url']: downloadURL
              }));
              
              // If logo, update auth user photoURL
              if (type === 'logo') {
                const auth = getAuth();
                await updateProfile(auth.currentUser, {
                  photoURL: downloadURL
                });
              }
              
              setShowToast({ 
                show: true, 
                message: `${type === 'logo' ? 'Logo' : 'Banner'} updated successfully`,
                type: "success" 
              });
              
              // Auto-dismiss the success message
              setTimeout(() => {
                setShowToast(prev => ({ ...prev, show: false }));
              }, 3000);
              
              resolve(downloadURL);
            } catch (error) {
              console.error(`Error updating ${type}:`, error);
              setError(`Failed to update ${type} URL: ${error.message}`);
              setShowToast({
                show: true,
                message: `Failed to update ${type}: ${error.message}`,
                type: 'error'
              });
              reject(error);
            } finally {
              setIsUploading(false);
              setUploadProgress(0);
            }
          }
        );
      });
      
    } catch (error) {
      console.error(`Upload error (${type}):`, error);
      setError(`Failed to upload ${type}. Please try again.`);
      setShowToast({
        show: true,
        message: `Failed to upload ${type}. Please try again.`,
        type: 'error'
      });
      setIsUploading(false);
      setUploadProgress(0);
      return null;
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (file) => {
    if (!file) return null;
    
    try {
      setIsUploading(true);
      setUploadType('logo');
      
      // Create a unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `org-logos/${user.uid}/logo-${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      // Set metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'owner': user.uid,
          'uploadedBy': user.uid,
          'uploadedAt': new Date().toISOString()
        }
      };
      
      // Upload file with metadata
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      // Return a promise that resolves with the download URL
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Track upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Error uploading logo:', error);
            setError('Failed to upload logo. Please try again.');
            setIsUploading(false);
            reject(error);
          },
          async () => {
            // Upload completed successfully
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Update organization profile with new logo URL
            const orgRef = doc(db, 'organizations', user.uid);
            await updateDoc(orgRef, {
              logoUrl: downloadURL,
              profileUpdatedAt: serverTimestamp()
            });
            
            // Update local state
            setOrgProfile(prev => ({
              ...prev,
              logoUrl: downloadURL
            }));
            
            setIsUploading(false);
            setShowToast({
              show: true,
              message: 'Logo updated successfully!',
              type: 'success'
            });
            
            resolve(downloadURL);
          }
        );
      });
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      setError('Failed to upload logo. Please try again.');
      setIsUploading(false);
      throw error;
    }
  };

  // Handle file input change for logo only
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }
    
    // Validate file size (max 5MB for logo)
    const maxSize = 5; // 5MB
    if (file.size > maxSize * 1024 * 1024) {
      setError(`Image size should be less than ${maxSize}MB`);
      return;
    }
    
    handleLogoUpload(file);
  };
  
  // Toggle profile visibility
  const toggleProfileVisibility = async () => {
    try {
      const newVisibility = !orgProfile.isPublic;
      setOrgProfile({...orgProfile, isPublic: newVisibility});
      
      // Save the new visibility to Firestore
      const orgRef = doc(db, "organizations", user.uid);
      await updateDoc(orgRef, {
        isPublic: newVisibility,
        profileUpdatedAt: serverTimestamp()
      });
      
      setShowToast({
        show: true,
        message: `Profile is now ${newVisibility ? 'public' : 'private'}`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error updating profile visibility:', error);
      setShowToast({
        show: true,
        message: 'Failed to update profile visibility. Please try again.',
        type: 'error'
      });
    }
  };

  if (loading && !initialFetchDone) return <Spinner />;
  if (!user && !loading) return <div className="text-center p-8">Please log in to view your profile.</div>;

  // Handle banner upload
  const handleBannerUpload = async (file) => {
    if (!file) return null;
    
    try {
      setIsUploading(true);
      setUploadType('banner');
      
      // Create a unique filename
      const fileExtension = file.name.split('.').pop();
      const fileName = `banners/${user.uid}/banner-${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, fileName);
      
      // Set metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'owner': user.uid,
          'uploadedBy': user.uid,
          'uploadedAt': new Date().toISOString()
        }
      };
      
      // Upload file with metadata
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      // Return a promise that resolves with the download URL
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Track upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            console.error('Error uploading banner:', error);
            setError('Failed to upload banner. Please try again.');
            setIsUploading(false);
            reject(error);
          },
          async () => {
            // Upload completed successfully
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Update organization profile with new banner URL
            const orgRef = doc(db, 'organizations', user.uid);
            await updateDoc(orgRef, {
              bannerUrl: downloadURL,
              profileUpdatedAt: serverTimestamp()
            });
            
            // Update local state
            setOrgProfile(prev => ({
              ...prev,
              bannerUrl: downloadURL
            }));
            
            setIsUploading(false);
            setShowToast({
              show: true,
              message: 'Banner updated successfully!',
              type: 'success'
            });
            
            resolve(downloadURL);
          }
        );
      });
      
    } catch (error) {
      console.error('Error uploading banner:', error);
      setError('Failed to upload banner. Please try again.');
      setIsUploading(false);
      throw error;
    }
  };

  // Handle banner file selection
  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError("Please select a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }
    
    // Validate file size (max 2MB for banner)
    const maxSize = 2; // 2MB
    if (file.size > maxSize * 1024 * 1024) {
      setError(`Banner image size should be less than ${maxSize}MB`);
      return;
    }
    
    handleBannerUpload(file);
  };

  // Render the banner and profile header
  const renderProfileHeader = () => (
    <div className="relative bg-white rounded-t-xl shadow-sm">
      {/* Banner Section */}
      <div className="relative h-48 bg-gradient-to-r from-indigo-900 via-purple-800 to-indigo-900 overflow-hidden">
        {/* Banner Image */}
        {orgProfile.bannerUrl ? (
          <img 
            src={orgProfile.bannerUrl} 
            alt="Banner" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-indigo-800"></div>
        )}
        {/* Powered by asdflo text overlay */}
        <div className="absolute top-4 right-4 z-10 bg-white/80 px-3 py-1 rounded-full shadow text-xs font-semibold text-gray-700">
          Powered by <span className="font-bold">asdflo</span>
        </div>
        
        {/* Banner Upload Button (Edit Mode) */}
        {editMode && (
          <div className="absolute bottom-4 right-4 z-10">
            <label htmlFor="banner-upload" className="inline-flex items-center px-3 py-1.5 bg-white/90 hover:bg-white text-gray-800 text-sm font-medium rounded-full border border-white/20 cursor-pointer transition-colors shadow-md">
              <FaCamera className="mr-2" />
              {orgProfile.bannerUrl ? 'Change Banner' : 'Add Banner'}
            </label>
            <input
              id="banner-upload"
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleBannerChange}
              disabled={isUploading && uploadType === 'banner'}
            />
            {isUploading && uploadType === 'banner' && (
              <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin ml-2 inline-block"></div>
            )}
          </div>
        )}
      </div>
      
      {/* Logo and Profile Info Section */}
      <div className="relative px-6 md:px-8 pb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end -mt-16 relative z-10">
          <div className="flex flex-col items-start">
            {/* Logo Container */}
            <div className="relative group mb-3">
              <div className="w-32 h-32 rounded-lg bg-white border-4 border-white shadow-xl overflow-hidden">
                {orgProfile.logoUrl ? (
                  <img 
                    src={orgProfile.logoUrl} 
                    alt="Logo" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <FaBuilding className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>
              
              {editMode && (
                <div className="absolute inset-0 rounded-lg bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <label htmlFor="logo-upload" className="cursor-pointer p-2 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-md">
                    <FaCamera className="text-lg" />
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                      disabled={isUploading && uploadType === 'logo'}
                    />
                  </label>
                </div>
              )}
              
              {isUploading && uploadType === 'logo' && (
                <div className="absolute inset-0 rounded-lg bg-black/70 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            
            {/* Organization Info - Moved below logo */}
            <div className="w-full">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-left">
                {orgProfile.orgName || 'Organization Name'}
              </h1>
              {orgProfile.orgType && (
                <p className="text-gray-600 mt-1 text-left">
                  {orgTypeOptions.find(t => t.value === orgProfile.orgType)?.label}
                </p>
              )}
            </div>
          </div>
          
          {/* Profile Visibility Toggle - Moved to right side */}
          <div className="mt-4 sm:mt-0 flex items-center bg-white/90 px-4 py-2 rounded-lg shadow-sm border border-gray-100">
            <Switch
              checked={orgProfile.isPublic || false}
              onChange={toggleProfileVisibility}
              className={`${
                orgProfile.isPublic ? 'bg-blue-600' : 'bg-gray-200'
              } relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            >
              <span className="sr-only">Toggle profile visibility</span>
              <span
                className={`${
                  orgProfile.isPublic ? 'translate-x-4' : 'translate-x-0'
                } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="ml-2 text-sm font-medium text-gray-700">
              {orgProfile.isPublic ? 'Public' : 'Private'} Profile
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Tabs Navigation
  const Tabs = ({ activeTab, setActiveTab, editMode, onEditToggle }) => {
    const tabs = [
      { id: 'overview', label: 'Overview', icon: <FaHome /> },
      { id: 'about', label: 'About', icon: <FaInfoCircle /> },
      { id: 'services', label: 'Services', icon: <FaHandsHelping /> },
      { id: 'locations', label: 'Locations', icon: <FaMapMarkerAlt /> },
    ];

    return (
      <div className="relative">
        <nav className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="text-base">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
        <button 
          onClick={onEditToggle}
          className="absolute top-4 right-4 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-md transition-colors duration-150 flex items-center justify-center"
          title={editMode ? "Save changes" : "Edit profile"}
        >
          {editMode ? (
            <FaSave className="text-sm" />
          ) : (
            <FaEdit className="text-sm" />
          )}
        </button>
      </div>
    );
  };

  // Render Locations Tab
  const renderLocationsTab = () => {
    if (editMode) {
      return (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
            <div className="space-y-4">
              {orgProfile.locations?.map((location, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                      <input
                        type="text"
                        value={location.name}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].name = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Head Office, Branch"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        value={location.address}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].address = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={location.city}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].city = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                      <input
                        type="text"
                        value={location.state}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].state = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="State/Province"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={location.country}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].country = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Country"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                      <input
                        type="text"
                        value={location.postalCode}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].postalCode = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Postal code"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={location.phone}
                        onChange={(e) => {
                          const newLocations = [...orgProfile.locations];
                          newLocations[index].phone = e.target.value;
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => {
                          const newLocations = orgProfile.locations.filter((_, i) => i !== index);
                          setOrgProfile({...orgProfile, locations: newLocations});
                        }}
                        className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Remove Location
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <h3 className="text-sm font-medium text-gray-700">Add a new location</h3>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="sr-only">Location Name</label>
                    <input
                      type="text"
                      value={currentLocation.name}
                      onChange={(e) => setCurrentLocation({...currentLocation, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Location name"
                    />
                  </div>
                  <div>
                    <label className="sr-only">Address</label>
                    <input
                      type="text"
                      value={currentLocation.address}
                      onChange={(e) => setCurrentLocation({...currentLocation, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="sr-only">City</label>
                    <input
                      type="text"
                      value={currentLocation.city}
                      onChange={(e) => setCurrentLocation({...currentLocation, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="sr-only">State/Province</label>
                    <input
                      type="text"
                      value={currentLocation.state}
                      onChange={(e) => setCurrentLocation({...currentLocation, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State/Province"
                    />
                  </div>
                  <div>
                    <label className="sr-only">Country</label>
                    <input
                      type="text"
                      value={currentLocation.country}
                      onChange={(e) => setCurrentLocation({...currentLocation, country: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Country"
                    />
                  </div>
                  <div>
                    <label className="sr-only">Postal Code</label>
                    <input
                      type="text"
                      value={currentLocation.postalCode}
                      onChange={(e) => setCurrentLocation({...currentLocation, postalCode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Postal code"
                    />
                  </div>
                  <div>
                    <label className="sr-only">Phone</label>
                    <input
                      type="tel"
                      value={currentLocation.phone}
                      onChange={(e) => setCurrentLocation({...currentLocation, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        if (currentLocation.name && currentLocation.address) {
                          const newLocations = [...(orgProfile.locations || []), {...currentLocation}];
                          setOrgProfile({...orgProfile, locations: newLocations});
                          setCurrentLocation({
                            name: '',
                            address: '',
                            city: '',
                            state: '',
                            country: '',
                            postalCode: '',
                            phone: ''
                          });
                        }
                      }}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Add Location
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setEditMode(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleOrgSave}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Locations
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Our Locations</h2>
          <button
            onClick={() => setEditMode(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaEdit className="mr-1" /> Edit Locations
          </button>
        </div>
        
        {orgProfile.locations?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orgProfile.locations.map((location, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-600">
                      <FaMapMarkerAlt className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{location.name}</h3>
                    <div className="mt-1 text-sm text-gray-600">
                      <p>{location.address}</p>
                      <p>
                        {[location.city, location.state, location.postalCode].filter(Boolean).join(', ')}
                      </p>
                      <p>{location.country}</p>
                      {location.phone && (
                        <p className="mt-2">
                          <a href={`tel:${location.phone}`} className="text-blue-600 hover:underline">
                            {location.phone}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FaMapMarkerAlt className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No locations added</h3>
            <p className="mt-1 text-sm text-gray-500">Add your organization's locations to help people find you.</p>
            <div className="mt-6">
              <button
                onClick={() => setEditMode(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaPlus className="-ml-1 mr-2 h-4 w-4" />
                Add Location
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render organization info section
  const renderOrgInfo = () => (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{orgProfile.orgName || 'Organization Name'}</h3>
          {orgProfile.orgType && (
            <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mt-2">
              {orgTypeOptions.find(opt => opt.value === orgProfile.orgType)?.label || orgProfile.orgType}
            </span>
          )}
        </div>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaEdit className="mr-1" /> Edit Profile
          </button>
        )}
      </div>
      
      {orgProfile.description && (
        <p className="mt-3 text-gray-600">
          {orgProfile.description}
        </p>
      )}
    </div>
  );

  // Render Overview Tab
  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* About Section */}
      <Section title="About Us">
        {orgProfile.aboutUs ? (
          <div className="prose max-w-none">
            <p className="text-gray-700">{orgProfile.aboutUs}</p>
          </div>
        ) : (
          <p className="text-gray-500 italic">No description provided.</p>
        )}
        
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {orgProfile.yearFounded && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500">Founded</p>
              <p className="font-medium">{orgProfile.yearFounded}</p>
            </div>
          )}
          {orgProfile.teamSize && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500">Team Size</p>
              <p className="font-medium">{orgProfile.teamSize}</p>
            </div>
          )}
          {orgProfile.locations?.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500">Locations</p>
              <p className="font-medium">{orgProfile.locations.length} {orgProfile.locations.length === 1 ? 'Location' : 'Locations'}</p>
            </div>
          )}
          {orgProfile.teamMembers?.length > 0 && (
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500">Team Members</p>
              <p className="font-medium">{orgProfile.teamMembers.length} {orgProfile.teamMembers.length === 1 ? 'Member' : 'Members'}</p>
            </div>
          )}
        </div>
      </Section>
      
      {/* Services Section */}
      <Section title="Our Services">
        {orgProfile.services?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orgProfile.services.slice(0, 6).map((service, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <h4 className="font-medium text-gray-900">{service.name}</h4>
                {service.description && (
                  <p className="mt-1 text-sm text-gray-600 line-clamp-2">{service.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {service.isOfferedInPerson && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      In-Person
                    </span>
                  )}
                  {service.isOfferedOnline && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Online
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic">No services listed yet.</p>
        )}
        {orgProfile.services?.length > 6 && (
          <div className="mt-4 text-center">
            <button 
              onClick={() => setActiveTab('services')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View all services ({orgProfile.services.length})
            </button>
          </div>
        )}
      </Section>
      
      {/* Impact Section */}
      {orgProfile.impact && (
        <Section title="Our Impact">
          <div className="prose max-w-none">
            <p className="text-gray-700">{orgProfile.impact}</p>
          </div>
        </Section>
      )}
      
      {/* Team Section */}
      {orgProfile.teamMembers?.length > 0 && (
        <Section title="Meet Our Team">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {orgProfile.teamMembers.slice(0, 5).map((member, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-500 mb-2 overflow-hidden">
                  {member.photoUrl ? (
                    <img src={member.photoUrl} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    member.name.charAt(0).toUpperCase()
                  )}
                </div>
                <h4 className="font-medium text-gray-900">{member.name}</h4>
                {member.role && (
                  <p className="text-sm text-gray-600">{member.role}</p>
                )}
              </div>
            ))}
          </div>
          {orgProfile.teamMembers.length > 5 && (
            <div className="mt-4 text-center">
              <button 
                onClick={() => setActiveTab('team')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View full team ({orgProfile.teamMembers.length} members)
              </button>
            </div>
          )}
        </Section>
      )}
      
      {/* Locations Section */}
      {orgProfile.locations?.length > 0 && (
        <Section title="Our Locations">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orgProfile.locations.slice(0, 3).map((location, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <h4 className="font-medium text-gray-900">{location.name}</h4>
                <div className="mt-2 text-sm text-gray-600 space-y-1">
                  <p>{location.address}</p>
                  <p>{[location.city, location.state, location.postalCode].filter(Boolean).join(', ')}</p>
                  <p>{location.country}</p>
                  {location.phone && (
                    <p className="mt-2">
                      <a href={`tel:${location.phone}`} className="text-blue-600 hover:underline">
                        {location.phone}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {orgProfile.locations.length > 3 && (
            <div className="mt-4 text-center">
              <button 
                onClick={() => setActiveTab('locations')}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View all locations ({orgProfile.locations.length})
              </button>
            </div>
          )}
        </Section>
      )}
      
      {/* Contact Section */}
      <Section title="Contact Us">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Get in Touch</h4>
            
            {renderContactInfo()}
            
            {/* Additional contact info that doesn't need masking */}
            <div className="space-y-2 text-gray-600 mt-4">
              {orgProfile.website && (
                <p className="flex items-start">
                  <FaGlobe className="mt-1 mr-2 flex-shrink-0 text-gray-400" />
                  <a 
                    href={orgProfile.website.startsWith('http') ? orgProfile.website : `https://${orgProfile.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline break-all"
                  >
                    {orgProfile.website}
                  </a>
                </p>
              )}
              
              {orgProfile.workingHours && (
                <p className="flex items-start">
                  <FaClock className="mt-1 mr-2 flex-shrink-0 text-gray-400" />
                  <span>
                    {orgProfile.workingDays && `${orgProfile.workingDays}: `}
                    {orgProfile.workingHours}
                  </span>
                </p>
              )}
            </div>
            
            {/* Social Media Links */}
            {(orgProfile.socialMedia?.facebook || orgProfile.socialMedia?.twitter || 
              orgProfile.socialMedia?.linkedin || orgProfile.socialMedia?.instagram || 
              orgProfile.socialMedia?.youtube) && (
              <div className="mt-4">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Follow Us</h5>
                <div className="flex space-x-4">
                  {orgProfile.socialMedia.facebook && (
                    <a href={orgProfile.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-600">
                      <FaFacebook className="h-5 w-5" />
                    </a>
                  )}
                  {orgProfile.socialMedia.twitter && (
                    <a href={orgProfile.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-400">
                      <FaTwitter className="h-5 w-5" />
                    </a>
                  )}
                  {orgProfile.socialMedia.linkedin && (
                    <a href={orgProfile.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-700">
                      <FaLinkedin className="h-5 w-5" />
                    </a>
                  )}
                  {orgProfile.socialMedia.instagram && (
                    <a href={orgProfile.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-600">
                      <FaInstagram className="h-5 w-5" />
                    </a>
                  )}
                  {orgProfile.socialMedia.youtube && (
                    <a href={orgProfile.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-red-600">
                      <FaYoutube className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
  
  // Render About Tab
  const renderAboutTab = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">About Us</h2>
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaEdit className="mr-1" /> Edit
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => setEditMode(false)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleOrgSave}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
        
        {editMode ? (
          <div className="space-y-6">
            {/* Organization Name */}
            <div>
              <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-1">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="orgName"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={orgProfile.orgName || ''}
                onChange={(e) => setOrgProfile({...orgProfile, orgName: e.target.value})}
                required
              />
            </div>

            {/* Organization Type */}
            <div>
              <label htmlFor="orgType" className="block text-sm font-medium text-gray-700 mb-1">
                Organization Type <span className="text-red-500">*</span>
              </label>
              <select
                id="orgType"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={orgProfile.orgType || ''}
                onChange={(e) => setOrgProfile({...orgProfile, orgType: e.target.value})}
                required
              >
                {orgTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* About Organization */}
            <div>
              <label htmlFor="aboutUs" className="block text-sm font-medium text-gray-700 mb-1">
                About Your Organization
              </label>
              <textarea
                id="aboutUs"
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Tell us about your organization, its mission, and values..."
                value={orgProfile.aboutUs || ''}
                onChange={(e) => setOrgProfile({...orgProfile, aboutUs: e.target.value})}
              />
              <p className="mt-1 text-xs text-gray-500">
                This will be displayed on your public profile.
              </p>
            </div>
            
            <div>
              <label htmlFor="yearFounded" className="block text-sm font-medium text-gray-700 mb-1">
                Year Founded
              </label>
              <input
                type="number"
                id="yearFounded"
                min="1900"
                max={new Date().getFullYear()}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={orgProfile.yearFounded || ''}
                onChange={(e) => setOrgProfile({...orgProfile, yearFounded: e.target.value})}
              />
            </div>
            
            <div>
              <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-1">
                Team Size
              </label>
              <select
                id="teamSize"
                className="w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={orgProfile.teamSize || ''}
                onChange={(e) => setOrgProfile({...orgProfile, teamSize: e.target.value})}
              >
                <option value="">Select team size</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1001-5000">1001-5000 employees</option>
                <option value="5001+">5000+ employees</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                Our Impact
              </label>
              <textarea
                id="impact"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Share the impact your organization has made..."
                value={orgProfile.impact || ''}
                onChange={(e) => setOrgProfile({...orgProfile, impact: e.target.value})}
              />
              <p className="mt-1 text-xs text-gray-500">
                Describe the positive changes or outcomes your organization has achieved.
              </p>
            </div>
            
            <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOrgSave}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {orgProfile.aboutUs ? (
              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-line">{orgProfile.aboutUs}</p>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FaInfoCircle className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No about information</h3>
                <p className="mt-1 text-sm text-gray-500">Add information about your organization to help others learn more about you.</p>
              </div>
            )}
            
            {(orgProfile.yearFounded || orgProfile.teamSize) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {orgProfile.yearFounded && (
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Founded</p>
                    <p className="font-medium">{orgProfile.yearFounded}</p>
                  </div>
                )}
                {orgProfile.teamSize && (
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-500">Team Size</p>
                    <p className="font-medium">{orgProfile.teamSize}</p>
                  </div>
                )}
              </div>
            )}
            
            {orgProfile.impact && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Our Impact</h3>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-line">{orgProfile.impact}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Services Tab
  const renderServicesTab = () => {
    const handleServiceChange = (e) => {
      const { name, value, type, checked } = e.target;
      setCurrentService(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    };

    const handleAddService = () => {
      if (!currentService.name || !currentService.category) {
        setShowToast({ show: true, message: 'Please fill in all required fields', type: 'error' });
        return;
      }

      const newServices = [...(orgProfile.services || [])];
      
      if (editingIndex !== null) {
        // Update existing service
        newServices[editingIndex] = { ...currentService };
      } else {
        // Add new service
        newServices.push({ ...currentService });
      }


      setOrgProfile(prev => ({
        ...prev,
        services: newServices
      }));

      // Reset form
      setCurrentService({
        name: '',
        description: '',
        category: '',
        isOfferedInPerson: false,
        isOfferedOnline: false
      });
      setEditingIndex(null);
    };

    const handleEditService = (index) => {
      setCurrentService({ ...orgProfile.services[index] });
      setEditingIndex(index);
    };

    const handleRemoveService = (index) => {
      if (window.confirm('Are you sure you want to remove this service?')) {
        const newServices = [...orgProfile.services];
        newServices.splice(index, 1);
        setOrgProfile(prev => ({
          ...prev,
          services: newServices
        }));
      }
    };

    const getServiceIcon = (category) => {
      if (!category) return <FaHandsHelping className="text-gray-500" />;
      
      switch (category.toLowerCase()) {
        case 'assessment':
          return <FaClipboardCheck className="text-blue-500" />;
        case 'behavioral therapy':
          return <FaHandsHelping className="text-green-500" />;
        case 'speech therapy':
          return <FaCommentAlt className="text-purple-500" />;
        case 'occupational therapy':
          return <FaTools className="text-yellow-500" />;
        case 'special education':
          return <FaGraduationCap className="text-red-500" />;
        case 'parent training':
          return <FaUsers className="text-teal-500" />;
        case 'social skills':
          return <FaUserFriends className="text-pink-500" />;
        case 'counseling':
          return <FaComments className="text-indigo-500" />;
        default:
          return <FaHandsHelping className="text-gray-500" />;
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Our Services</h2>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaEdit className="mr-1" /> Edit Services
            </button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingIndex !== null ? 'Edit Service' : 'Add New Service'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    id="serviceName"
                    name="name"
                    value={currentService.name}
                    onChange={handleServiceChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Speech Therapy"
                  />
                </div>

                <div>
                  <label htmlFor="serviceCategory" className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <select
                    id="serviceCategory"
                    name="category"
                    value={currentService.category}
                    onChange={handleServiceChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a category</option>
                    {serviceCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="serviceDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="serviceDescription"
                    name="description"
                    rows={3}
                    value={currentService.description}
                    onChange={handleServiceChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the service in detail..."
                  />
                </div>

                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-gray-700 mb-2">Available As</p>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <input
                        id="inPerson"
                        name="isOfferedInPerson"
                        type="checkbox"
                        checked={currentService.isOfferedInPerson}
                        onChange={handleServiceChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="inPerson" className="ml-2 block text-sm text-gray-700">
                        In-Person
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="online"
                        name="isOfferedOnline"
                        type="checkbox"
                        checked={currentService.isOfferedOnline}
                        onChange={handleServiceChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="online" className="ml-2 block text-sm text-gray-700">
                        Online
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {editingIndex !== null && (
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentService({
                        name: '',
                        description: '',
                        category: '',
                        isOfferedInPerson: false,
                        isOfferedOnline: false
                      });
                      setEditingIndex(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAddService}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {editingIndex !== null ? 'Update Service' : 'Add Service'}
                </button>
              </div>
            </div>

            {orgProfile.services?.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Current Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orgProfile.services.map((service, index) => (
                    <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{service.name}</h4>
                          <p className="text-sm text-blue-600 font-medium">{service.category}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleEditService(index)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <FaEdit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveService(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove"
                          >
                            <FaTrash className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {service.description && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{service.description}</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {service.isOfferedInPerson && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            In-Person
                          </span>
                        )}
                        {service.isOfferedOnline && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            Online
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOrgSave}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Changes
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {orgProfile.services?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {orgProfile.services.map((service, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 bg-blue-50 p-3 rounded-lg">
                        {getServiceIcon(service.category)}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">{service.name}</h3>
                        <p className="text-sm text-blue-600 font-medium">{service.category}</p>
                        {service.description && (
                          <p className="mt-2 text-sm text-gray-600">{service.description}</p>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {service.isOfferedInPerson && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              In-Person
                            </span>
                          )}
                          {service.isOfferedOnline && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FaTools className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No services added yet</h3>
                <p className="mt-1 text-sm text-gray-500">Add services to showcase what your organization offers.</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return renderAboutTab();
      case 'services':
        return renderServicesTab();
      case 'team':
        return renderTeamTab();
      case 'locations':
        return renderLocationsTab();
      default:
        return renderOverviewTab();
    }
  };

  // Subscription Modal Component
  const SubscriptionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Upgrade Your Plan</h3>
          <button 
            onClick={() => setShowSubscriptionModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-4">
            Subscribe to access contact information and connect with organizations.
            Choose a plan that works for you:
          </p>
          <div className="space-y-3">
            <div className="border rounded-lg p-4 hover:border-blue-500 cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-medium">Basic</h4>
                  <p className="text-sm text-gray-500">View contact information</p>
                </div>
                <span className="text-lg font-bold">$9.99/month</span>
              </div>
            </div>
            <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50 cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-medium">Professional</h4>
                  <p className="text-sm text-gray-500">Full access to all features</p>
                </div>
                <span className="text-lg font-bold text-blue-600">$19.99/month</span>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <button
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Continue with Professional Plan
            </button>
            <p className="mt-2 text-xs text-center text-gray-500">
              7-day free trial. Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render contact info with masking for public view
  const renderContactInfo = () => {
    // Show editable fields in edit mode
    if (editMode) {
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <input
              type="email"
              value={orgProfile.contactEmail || ''}
              onChange={(e) => setOrgProfile({...orgProfile, contactEmail: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="contact@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
            <input
              type="tel"
              value={orgProfile.contactPhone || ''}
              onChange={(e) => setOrgProfile({...orgProfile, contactPhone: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="+1 (123) 456-7890"
            />
          </div>
        </div>
      );
    }
    
    // Show unmasked contact info for the profile owner
    if (user && user.uid === orgProfile.userId) {
      return (
        <div className="space-y-2">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700">Contact Email</p>
            <a 
              href={`mailto:${orgProfile.contactEmail || ''}`}
              className="text-blue-600 hover:underline"
            >
              {orgProfile.contactEmail || 'Not provided'}
            </a>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700">Contact Phone</p>
            {orgProfile.contactPhone ? (
              <a 
                href={`tel:${orgProfile.contactPhone}`}
                className="text-blue-600 hover:underline"
              >
                {orgProfile.contactPhone}
              </a>
            ) : (
              <p className="text-gray-500">Not provided</p>
            )}
          </div>
        </div>
      );
    }
    
    // Public view - show masked info with CTA
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-700">Contact Email</p>
            <p className="text-gray-500">•••••@•••••.com</p>
          </div>
          <button 
            onClick={() => setShowSubscriptionModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Subscribe to view
          </button>
        </div>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-700">Contact Phone</p>
            <p className="text-gray-500">••• ••• ••••</p>
          </div>
          <button 
            onClick={() => setShowSubscriptionModal(true)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Subscribe to view
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-8 px-4 sm:px-6 lg:px-8">
        {showToast.show && (
          <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-md shadow-lg text-white ${showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} z-50`}>
            {showToast.message}
          </div>
        )}

        <div className="max-w-6xl mx-auto">
          {/* Profile Header */}
          {renderProfileHeader()}
          
          {/* Main Content */}
          <div className="bg-white shadow-xl rounded-xl overflow-hidden">
            {/* Tabs Navigation */}
            <Tabs 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
              editMode={editMode} 
              onEditToggle={() => setEditMode(!editMode)} 
            />
            
            {/* Tab Content */}
            <div className="p-6 md:p-8">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
      {showSubscriptionModal && <SubscriptionModal />}
      <Footer />
    </>
  );
}
